/**
 * MediaSessionManager
 *
 * Manages Media Session API integration for lock screen controls
 * on mobile devices (iOS, Android) and desktop OS media widgets.
 *
 * Handles:
 * - Track metadata (title, artist, artwork)
 * - Playback state synchronization
 * - Action handlers (play, pause, next, previous, seek)
 * - Position state updates
 */

class MediaSessionManager {
  constructor() {
    this.isSupported = 'mediaSession' in navigator;

    if (!this.isSupported) {
      console.warn('Media Session API not supported in this browser');
    }
  }

  /**
   * Update the metadata shown in lock screen controls
   * @param {Object} track - Track object with title, artist, username, albumArtUrl
   */
  updateMetadata(track) {
    if (!this.isSupported || !track) return;

    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title || 'Unknown Title',
        artist: track.artist || 'Unknown Artist',
        album: track.username || 'Muzio',
        artwork: [
          { src: track.albumArtUrl, sizes: '96x96',   type: 'image/png' },
          { src: track.albumArtUrl, sizes: '128x128', type: 'image/png' },
          { src: track.albumArtUrl, sizes: '192x192', type: 'image/png' },
          { src: track.albumArtUrl, sizes: '256x256', type: 'image/png' },
          { src: track.albumArtUrl, sizes: '384x384', type: 'image/png' },
          { src: track.albumArtUrl, sizes: '512x512', type: 'image/png' },
        ]
      });

      console.log('Media Session metadata updated:', track.title, '-', track.artist);
    } catch (error) {
      console.error('Failed to update Media Session metadata:', error);
    }
  }

  /**
   * Register action handlers for lock screen controls
   * @param {Object} handlers - Object with play, pause, previoustrack, nexttrack, seekto handlers
   */
  setActionHandlers(handlers) {
    if (!this.isSupported) return;

    const actions = ['play', 'pause', 'previoustrack', 'nexttrack', 'seekto', 'seekbackward', 'seekforward'];

    actions.forEach(action => {
      try {
        navigator.mediaSession.setActionHandler(action, handlers[action] || null);
        if (handlers[action]) {
          console.log(`Media Session action handler registered: ${action}`);
        }
      } catch (error) {
        // Some actions might not be supported in all browsers
        console.warn(`Media Session action "${action}" not supported:`, error.message);
      }
    });
  }

  /**
   * Update the playback state shown in lock screen controls
   * @param {string} state - 'playing', 'paused', or 'none'
   */
  updatePlaybackState(state) {
    if (!this.isSupported) return;

    try {
      navigator.mediaSession.playbackState = state;
      console.log('Media Session playback state:', state);
    } catch (error) {
      console.error('Failed to update playback state:', error);
    }
  }

  /**
   * Update position state for lock screen seek bar
   * @param {number} duration - Total duration in seconds
   * @param {number} position - Current position in seconds
   * @param {number} playbackRate - Playback rate (default 1.0)
   */
  updatePositionState(duration, position, playbackRate = 1.0) {
    if (!this.isSupported || !('setPositionState' in navigator.mediaSession)) return;

    // Validate inputs
    if (!duration || isNaN(duration) || duration <= 0) return;
    if (isNaN(position) || position < 0) return;
    if (position > duration) position = duration;

    try {
      navigator.mediaSession.setPositionState({
        duration: duration,
        playbackRate: playbackRate,
        position: position
      });
    } catch (error) {
      // Position state might fail if values are invalid
      console.warn('Failed to update position state:', error.message);
    }
  }

  /**
   * Clear all metadata and reset Media Session
   */
  clear() {
    if (!this.isSupported) return;

    try {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
      console.log('Media Session cleared');
    } catch (error) {
      console.error('Failed to clear Media Session:', error);
    }
  }
}

// Export singleton instance
const mediaSessionManager = new MediaSessionManager();
export default mediaSessionManager;
